import {
  createStorefrontClient,
  type UserProfile,
  type LoginRequest,
  type RegisterRequest,
  type GoogleAuthRequest,
} from '@template-project/storefront-bff-client';
import { env } from '@/lib/utils/env';
import { storefrontLayoutLogger } from '@/lib/logger';

// Re-export request/response types so existing callers don't need to update imports
export type { UserProfile } from '@template-project/storefront-bff-client';
export type { LoginRequest } from '@template-project/storefront-bff-client';
export type { RegisterRequest } from '@template-project/storefront-bff-client';
export type { GoogleAuthRequest } from '@template-project/storefront-bff-client';

/** Shape returned by login/register/google/refresh — user info only (no token). */
export interface SecureLoginResponse {
  user: {
    displayName: string;
  };
}

export interface ErrorResponse {
  type: string;
  title: string;
  status: number;
  detail: string;
}

function getBffClient() {
  return createStorefrontClient({
    baseUrl: env.STOREFRONT_BFF_URL,
    tenantId: env.FUNNELS_TENANT_ID,
  });
}

export class AuthService {
  /**
   * Login with email and password.
   * BFF sets HttpOnly access_token cookie on success.
   */
  async login(request: LoginRequest): Promise<SecureLoginResponse> {
    const res = await getBffClient().login(request);
    this.storeSession(res.data.user.displayName);
    return { user: res.data.user };
  }

  /**
   * Register a new user account.
   * BFF sets HttpOnly access_token cookie on success.
   */
  async register(request: RegisterRequest): Promise<SecureLoginResponse> {
    const res = await getBffClient().register(request);
    this.storeSession(res.data.user.displayName);
    return { user: res.data.user };
  }

  /**
   * Login or register with Google OAuth.
   * BFF sets HttpOnly access_token cookie on success.
   */
  async loginWithGoogle(request: GoogleAuthRequest): Promise<SecureLoginResponse> {
    const res = await getBffClient().loginWithGoogle(request);
    this.storeSession(res.data.user.displayName);
    return { user: res.data.user };
  }

  /**
   * Refresh the session using the HttpOnly cookie managed by the BFF.
   * Returns { success, authError }.
   */
  async refreshAccessToken(): Promise<{ success: boolean; authError: boolean }> {
    if (!this.hasSession()) {
      return { success: false, authError: false };
    }
    try {
      const res = await getBffClient().refresh();
      this.storeSession(res.data.user.displayName);
      return { success: true, authError: false };
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      const isAuthError = status === 401 || status === 403;
      return { success: false, authError: isAuthError };
    }
  }

  /** Logout: tells the BFF to clear the HttpOnly cookie. */
  async logout(): Promise<void> {
    try {
      await getBffClient().logout();
    } catch {
      // Ignore upstream errors — always clear local session
    } finally {
      this.clearSession();
    }
  }

  /** Store displayName in localStorage so hasSession() survives page refresh. */
  storeSession(displayName: string): void {
    localStorage.setItem('displayName', displayName);
  }

  /** Clear all local session markers. */
  clearSession(): void {
    localStorage.removeItem('displayName');
  }

  /** Whether there's a potential session (displayName in localStorage). */
  hasSession(): boolean {
    return !!localStorage.getItem('displayName');
  }

  /** Display name from localStorage (null if logged out). */
  getDisplayName(): string | null {
    return localStorage.getItem('displayName');
  }

  /** Whether the user is authenticated (session marker present). */
  isAuthenticated(): boolean {
    return this.hasSession();
  }

  /**
   * Try to restore session after page refresh by asking the BFF to refresh.
   * Returns { success, authError }.
   */
  async tryRestoreSession(): Promise<{ success: boolean; authError: boolean }> {
    return this.refreshAccessToken();
  }

  /** Fetch the current user profile from the BFF (uses HttpOnly cookie). */
  async fetchCurrentUser(): Promise<UserProfile> {
    const res = await getBffClient().getMe();
    return res.data;
  }

  // ============================================
  // DEPRECATED — kept for backwards compatibility
  // ============================================

  /** @deprecated Use storeSession instead */
  storeTokens(): void {
    storefrontLayoutLogger.warn('storeTokens is deprecated.');
  }

  /** @deprecated Session is now cookie-based; no in-memory token. */
  getAccessToken(): null {
    return null;
  }

  /** @deprecated Refresh token is in HttpOnly cookie managed by BFF. */
  getRefreshToken(): null {
    return null;
  }

  /** @deprecated Use clearSession instead */
  clearTokens(): void {
    this.clearSession();
  }

  /** @deprecated Use fetchCurrentUser instead */
  getUser(): null {
    storefrontLayoutLogger.warn('getUser is deprecated. Use fetchCurrentUser.');
    return null;
  }

  /** @deprecated Use fetchCurrentUser instead */
  async getUserOrFetch(): Promise<UserProfile | null> {
    if (!this.hasSession()) return null;
    const result = await this.tryRestoreSession();
    if (!result.success) return null;
    try {
      return await this.fetchCurrentUser();
    } catch {
      return null;
    }
  }

  /** @deprecated No longer needed; token refresh is cookie-based. */
  async getValidAccessToken(): Promise<null> {
    return null;
  }
}

export const authService = new AuthService();
